import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Sparkles } from "lucide-react";

const STATUS = {
  published: ["Live", "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]"],
  changed: ["Edits not yet live", "border-[#D9B47A] text-[#D9B47A]"],
  draft: ["Draft · not live", "border-[#8F8176] text-[#CFC2B6]"],
  generating: ["Ariel is preparing…", "border-[#E8838E] text-[#E8838E]"],
  empty: ["Empty", "border-[#8F8176] text-[#A8998C]"],
};
const input = "w-full bg-[#0A0706] border border-[#D9B47A]/20 px-3 py-2.5 text-[15px] text-[#F2E8DF] placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A]";
const btn = "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-tt-mono text-[11px] uppercase tracking-[0.2em] transition-colors disabled:opacity-50";
const red = `${btn} bg-[#C2414F] text-[#F2E8DF] hover:bg-[#D9B47A] hover:text-[#0A0706]`;
const line = `${btn} border border-[#D9B47A]/50 text-[#D9B47A] hover:bg-[#D9B47A] hover:text-[#0A0706]`;
const rose = `${btn} border border-[#E8838E]/50 text-[#E8838E] hover:bg-[#E8838E] hover:text-[#0A0706]`;
const detail = (err) => (typeof err.response?.data?.detail === "string" ? err.response.data.detail : "That didn’t work. Please try again.");

const Chip = ({ s }) => {
  const [label, cls] = STATUS[s] ?? STATUS.empty;
  return <span className={`inline-block rounded-full border px-3 py-1 text-[12px] whitespace-nowrap ${cls}`}>{label}</span>;
};

const GatePicker = ({ names, value, onChange }) => (
  <div className="mt-3 flex flex-wrap gap-2">
    {names.map((n, g) => {
      const on = value.includes(g);
      return <button key={g} type="button" data-testid={`bm-gate-${g}`} aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== g) : [...value, g].sort((a, b) => a - b))}
        className={`rounded-full border px-3 py-1.5 text-[12px] transition-colors ${on ? "bg-[#C2414F] border-[#C2414F] text-[#F2E8DF]" : "border-[#D9B47A]/25 text-[#CFC2B6] hover:border-[#D9B47A]"}`}>{g + 1} · {n}</button>;
    })}
  </div>
);

const Editor = ({ k, http, withRefresh, onBack }) => {
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => withRefresh(() => http.get(`/admin/body-map/${k}`)).then(({ data }) => {
    setDoc(data);
    setDraft(data.draft);
    setDirty(false);
    return data;
  }), [k, http, withRefresh]);
  useEffect(() => {
    load().catch(() => toast.error("Could not open this area."));
  }, [load]);
  useEffect(() => {
    if (doc?.status !== "generating") return undefined;
    const t = setInterval(() => load().catch(() => {}), 4000);
    return () => clearInterval(t);
  }, [doc?.status, load]);
  const set = (patch) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save" || kind === "publish") await withRefresh(() => http.put(`/admin/body-map/${k}`, draft));
      if (kind !== "save") await withRefresh(() => http.post(`/admin/body-map/${k}/${kind}`));
      toast.success({ save: "Draft saved. Initiates still see the live version.", publish: "Published. Initiates now see this area.", unpublish: "Taken offline.", generate: "Ariel is preparing a fresh draft…" }[kind]);
      await load();
    } catch (err) {
      toast.error(detail(err));
    } finally {
      setBusy("");
    }
  };
  if (!doc || !draft) return <Loader2 className="mt-10 w-5 h-5 animate-spin text-[#D9B47A]" />;
  const gen = doc.status === "generating";
  return (
    <div data-testid="bm-editor" className="mt-8">
      <button data-testid="bm-back" onClick={onBack} className="inline-flex items-center gap-2 text-sm text-[#A8998C] hover:text-[#F2E8DF]"><ArrowLeft className="w-4 h-4" /> All areas</button>
      <div className="mt-5 flex flex-wrap items-center gap-3"><p className="font-display text-3xl leading-tight">{doc.name}</p><Chip s={doc.status} />{dirty && <span data-testid="bm-dirty" className="text-[12px] text-[#E8838E]">Unsaved changes</span>}</div>
      {doc.error && <p className="mt-2 text-sm text-[#E8838E]">{doc.error}</p>}
      <div className="sticky top-0 z-10 -mx-2 px-2 py-4 bg-[#0A0706]/95 backdrop-blur flex flex-wrap gap-2 border-b border-[#D9B47A]/15">
        <button data-testid="bm-save" onClick={() => act("save")} disabled={!!busy || gen} className={line}>{busy === "save" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save draft</button>
        <button data-testid="bm-publish" onClick={() => act("publish")} disabled={!!busy || gen} className={red}>{busy === "publish" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} {doc.has_published ? "Publish changes" : "Publish"}</button>
        {doc.has_published && <button data-testid="bm-unpublish" onClick={() => act("unpublish")} disabled={!!busy} className={`${btn} border border-[#8F8176] text-[#CFC2B6] hover:border-[#E8838E] hover:text-[#E8838E]`}>Take offline</button>}
        <button data-testid="bm-generate" onClick={() => window.confirm("Replace this draft with a fresh one from Ariel? Your live version is not affected until you publish.") && act("generate")} disabled={!!busy || gen} className={rose}>{gen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {gen ? "Preparing…" : "Re-prepare with Ariel"}</button>
      </div>
      <label className="mt-6 block text-[13px] text-[#A8998C]">Essence (one line)</label>
      <input data-testid="bm-essence" value={draft.essence ?? ""} maxLength={400} onChange={(e) => set({ essence: e.target.value })} className={`${input} mt-2`} />
      <p className="mt-6 text-[13px] text-[#A8998C]">Gates that awaken this area</p>
      <GatePicker names={doc.gates} value={draft.gates ?? []} onChange={(gates) => set({ gates })} />
      {doc.layers.map((l) => (
        <div key={l} className="mt-6">
          <label className="block font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">{l}</label>
          <textarea data-testid={`bm-layer-${l}`} value={draft.layers?.[l] ?? ""} maxLength={1500} rows={4} onChange={(e) => set({ layers: { ...draft.layers, [l]: e.target.value } })} className={`${input} mt-2 leading-relaxed`} />
        </div>
      ))}
    </div>
  );
};

// Admin: review, edit and publish the 13 Gates body map, area by area.
export default function BodyMapAdmin({ http, withRefresh }) {
  const [list, setList] = useState(null);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => withRefresh(() => http.get("/admin/body-map")).then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => toast.error("Could not load the body map.")), [http, withRefresh]);
  useEffect(() => {
    load();
  }, [load]);
  const preparing = (list ?? []).some((r) => r.status === "generating");
  useEffect(() => {
    if (!preparing || open) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [preparing, open, load]);
  if (open) return <Editor k={open} http={http} withRefresh={withRefresh} onBack={() => { setOpen(null); load(); }} />;
  const rows = list ?? [];
  const live = rows.filter((r) => r.status === "published" || r.status === "changed").length;
  const empty = rows.filter((r) => r.status === "empty").length;
  const pending = rows.filter((r) => r.status === "draft" || r.status === "changed").length;
  const run = async (kind) => {
    if (kind === "publish-all" && !window.confirm(`Publish ${pending} area${pending === 1 ? "" : "s"}? Initiates will see them straight away.`)) return;
    setBusy(kind);
    try {
      const { data } = await withRefresh(() => http.post(`/admin/body-map/${kind}`));
      toast.success(kind === "publish-all" ? `${data?.published?.length ?? 0} areas published.` : `Ariel is preparing ${data?.preparing?.length ?? 0} areas. This takes a minute or two.`);
      load();
    } catch (err) {
      toast.error(detail(err));
    } finally {
      setBusy("");
    }
  };
  return (
    <div data-testid="bodymap-admin" className="mt-10">
      <p className="text-[#CFC2B6] max-w-2xl">The 13 Gates body map. Initiates tap an area of a male or female figure and read it through eight layers. Ariel drafts each area from Book I and Book II; review, edit and publish. <span data-testid="bm-live-count" className="text-[#D9B47A]">{live} of {rows.length || 9} areas live.</span></p>
      <div className="mt-6 flex flex-wrap gap-2">
        {empty > 0 && <button data-testid="bm-generate-all" onClick={() => run("generate-all")} disabled={!!busy} className={rose}>{busy === "generate-all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Prepare {empty} empty area{empty === 1 ? "" : "s"} with Ariel</button>}
        {pending > 0 && <button data-testid="bm-publish-all" onClick={() => run("publish-all")} disabled={!!busy} className={red}>{busy === "publish-all" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Publish all ({pending})</button>}
      </div>
      <ul className="mt-8 border-t border-[#D9B47A]/15">
        {rows.map((r) => (
          <li key={r.key}>
            <button data-testid={`bm-area-${r.key}`} onClick={() => setOpen(r.key)} className="w-full text-left flex flex-wrap items-center justify-between gap-3 py-4 px-2 border-b border-[#D9B47A]/15 hover:bg-[#120C0A] transition-colors">
              <span className="min-w-0">
                <span className="block font-display text-xl">{r.name}</span>
                <span className="block font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#A8998C]">{r.gates.length ? `Gates ${r.gates.map((g) => g + 1).join(", ")}` : "No gates linked yet"}</span>
                {r.error && <span className="block text-[12px] text-[#E8838E]">{r.error}</span>}
              </span>
              <Chip s={r.status} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
